import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IRegionDTO } from '@common-frontend-submodule/interfaces/region/iregion.interface';
import { IRegionCreationDTO } from '@common-frontend-submodule/interfaces/region/iregion-creation.interface';
import { IRegionUpdateDTO } from '@common-frontend-submodule/interfaces/region/iregion-update.interface';
import { IGetRegionsRequestDTO } from '@common-frontend-submodule/interfaces/region/iget-regions-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { RegionRoutes } from '@common-frontend-submodule/enums/routes/region-routes.enum';

const apiClient = ApiClient.getInstance()

interface RegionContextProps {
  getById: (regionId: string) => Promise<IApiResponseGenericDTO<IRegionDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IRegionDTO[]>>;
  filter: (model: IGetRegionsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IRegionDTO>>>;
  create: (model: IRegionCreationDTO) => Promise<IApiResponseGenericDTO<IRegionDTO>>;
  update: (regionId: string, model: IRegionUpdateDTO) => Promise<IApiResponseGenericDTO<IRegionDTO>>;
  delete: (regionId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const RegionContext = createContext<RegionContextProps | undefined>(undefined);

export const RegionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (regionId: string): Promise<IApiResponseGenericDTO<IRegionDTO>> => {
    return await apiClient.getRequest<IRegionDTO>(`${RegionRoutes.BasePrefix}/${regionId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IRegionDTO[]>> => {
    return await apiClient.getRequest<IRegionDTO[]>(`${RegionRoutes.BasePrefix}/${RegionRoutes.All}`);
  };

  const filter = async (model: IGetRegionsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IRegionDTO>>> => {
    return await apiClient.postRequest<IGetRegionsRequestDTO, IPaginatedResultDTO<IRegionDTO>>(
      `${RegionRoutes.BasePrefix}/${RegionRoutes.List}`,
      model
    );
  };

  const create = async (model: IRegionCreationDTO): Promise<IApiResponseGenericDTO<IRegionDTO>> => {
    return await apiClient.postRequest<IRegionCreationDTO, IRegionDTO>(RegionRoutes.BasePrefix, model);
  };

  const update = async (regionId: string, model: IRegionUpdateDTO): Promise<IApiResponseGenericDTO<IRegionDTO>> => {
    return await apiClient.putRequest<IRegionUpdateDTO, IRegionDTO>(`${RegionRoutes.BasePrefix}/${regionId}`, model);
  };

  const deleteRegion = async (regionId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${RegionRoutes.BasePrefix}/${regionId}`);
  };

  return (
    <RegionContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteRegion }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegionStore = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegionStore must be used within a RegionProvider');
  }
  return context;
};
